import { MerkleTree as MerkleTreeJS } from 'merkletreejs';
import { keccak256, encodePacked, toBytes } from 'viem';

/**
 * Utility for building Merkle trees and generating proofs using merkletreejs.
 * Uses keccak256(abi.encodePacked(uint256)) for leaf hashing to match
 * Stash contract expectations.
 */

/** Matches bids.cryptopunks.app `POST /api/v1/merkle/root` and pair-sorted verification (Solady `MerkleProofLib`). */
const MERKLE_TREE_OPTIONS = {
  sortPairs: true,
  sortLeaves: true,
  duplicateOdd: true
} as const;

/**
 * Hash a punk index to create a leaf for the Merkle tree.
 * Matches Solidity: keccak256(abi.encodePacked(uint256))
 */
function hashPunkIndex(punkIndex: number): Buffer {
  const hash = keccak256(encodePacked(['uint256'], [BigInt(punkIndex)]));
  return Buffer.from(toBytes(hash));
}

/**
 * Keccak256 hash function for merkletreejs (takes Buffer, returns Buffer)
 */
function keccak256Buffer(data: Buffer): Buffer {
  // Convert buffer to hex string, hash it, return as buffer
  const hexData = `0x${data.toString('hex')}` as `0x${string}`;
  const hash = keccak256(hexData);
  return Buffer.from(toBytes(hash));
}

/**
 * Numeric ascending order before leaves; duplicates kept (multiset).
 * Matches the index order used when posting `punkIndices` after sorting.
 */
function orderPunkIndicesForMerkle(punkIndices: number[]): number[] {
  return [...punkIndices].sort((a, b) => a - b);
}

/**
 * Merkle root only — avoids per-index proofs (use for full-collection bids, 10k leaves).
 */
export function buildMerkleRootForPunks(punkIndices: number[]): `0x${string}` {
  if (punkIndices.length === 0) {
    return '0x0000000000000000000000000000000000000000000000000000000000000000';
  }
  const ordered = orderPunkIndicesForMerkle(punkIndices);
  const leaves = ordered.map((index) => hashPunkIndex(index));
  const tree = new MerkleTreeJS(leaves, keccak256Buffer, { ...MERKLE_TREE_OPTIONS });
  return `0x${tree.getRoot().toString('hex')}` as `0x${string}`;
}

/**
 * Build a Merkle tree from punk indices and return root and proofs.
 *
 * Uses `MERKLE_TREE_OPTIONS`: same roots as cryptopunks.app bid verification and
 * pair-sorted hashing compatible with Stash / `MerkleProofLib.verifyCalldata`.
 *
 * Debug: `yarn debug:merkle-root` / `node scripts/debug-merkle-root.mjs '[...]'`
 */
export function buildMerkleTreeForPunks(punkIndices: number[]): {
  root: `0x${string}`;
  proofs: Record<number, `0x${string}`[]>;
} {
  if (punkIndices.length === 0) {
    return {
      root: '0x0000000000000000000000000000000000000000000000000000000000000000',
      proofs: {}
    };
  }

  const ordered = orderPunkIndicesForMerkle(punkIndices);

  // Create leaves: keccak256(abi.encodePacked(uint256(punkIndex)))
  const leaves = ordered.map((index) => hashPunkIndex(index));

  const tree = new MerkleTreeJS(leaves, keccak256Buffer, { ...MERKLE_TREE_OPTIONS });

  // Get the root as hex string
  const root = `0x${tree.getRoot().toString('hex')}` as `0x${string}`;

  const proofs: Record<number, `0x${string}`[]> = {};
  for (const punkIndex of ordered) {
    const leaf = hashPunkIndex(punkIndex);
    const proof = tree.getHexProof(leaf) as `0x${string}`[];
    proofs[punkIndex] = proof;
  }

  return { root, proofs };
}

/**
 * Get the leaf hash for a punk index (useful for verification)
 */
export function getPunkLeaf(punkIndex: number): `0x${string}` {
  return keccak256(encodePacked(['uint256'], [BigInt(punkIndex)]));
}

/**
 * Verify a proof for a given punk index against a root
 */
export function verifyPunkProof(
  punkIndex: number,
  proof: `0x${string}`[],
  root: `0x${string}`
): boolean {
  const leaf = hashPunkIndex(punkIndex);
  const proofBuffers = proof.map(p => Buffer.from(toBytes(p)));
  const rootBuffer = Buffer.from(toBytes(root));
  
  return MerkleTreeJS.verify(proofBuffers, leaf, rootBuffer, keccak256Buffer, {
    sortPairs: true
  });
}
